'use client';

import React, { useState, useEffect } from 'react';
import { 
  Table, 
  Button, 
  Paper, 
  Title, 
  TextInput, 
  Group, 
  Text,
  ActionIcon,
  Menu,
  Loader,
  Modal,
  Stack,
  Tabs,
  Avatar,
  Badge,
  Select
} from '@mantine/core';
import { IconDotsVertical, IconEdit, IconTrash, IconUserPlus, IconUsers } from '@tabler/icons-react';
import { useAuth } from '../hooks/useAuth';
import { 
  updateOrganization, 
  deleteOrganization,
  addUserToOrganization,
  removeUserFromOrganization,
  getOrganizationMembers
} from '../services/firestore';
import { Organization, UserProfile } from '../types';
import { CreateOrganization } from './CreateOrganization';

interface OrganizationManagementProps {
  onError?: (error: Error) => void;
}

export const OrganizationManagement: React.FC<OrganizationManagementProps> = ({ 
  onError 
}) => {
  const { user, organizations, loadOrganizations } = useAuth();
  const [loading, setLoading] = useState(true);
  const [editingOrg, setEditingOrg] = useState<Organization | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
  const [orgMembers, setOrgMembers] = useState<UserProfile[]>([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  
  useEffect(() => {
    const loadOrgs = async () => {
      if (!user) {
        setLoading(false);
        return;
      }
      
      setLoading(true);
      
      try {
        if (loadOrganizations) {
          await loadOrganizations();
          
          if (organizations.length > 0 && !selectedOrgId) {
            setSelectedOrgId(organizations[0].id);
          }
        }
      } catch (error) {
        console.error('Error loading organizations:', error);
        onError?.(error as Error);
      } finally {
        setLoading(false);
      }
    };
    
    loadOrgs();
  }, [user, onError, selectedOrgId, loadOrganizations, organizations]);
  
  useEffect(() => {
    const loadMembers = async () => {
      if (!selectedOrgId) return;
      
      try {
        const members = await getOrganizationMembers(selectedOrgId);
        setOrgMembers(members);
      } catch (error) {
        console.error('Error loading organization members:', error);
        onError?.(error as Error);
      }
    };
    
    loadMembers();
  }, [selectedOrgId, onError]);
  
  const handleEditOrg = (org: Organization) => {
    setEditingOrg(org);
    setIsEditModalOpen(true);
  };
  
  const handleSaveOrg = async () => {
    if (!editingOrg) return;
    
    try {
      await updateOrganization(editingOrg.id, { name: editingOrg.name });
      
      // Update local state
      await loadOrganizations();
      
      setIsEditModalOpen(false);
    } catch (error) {
      console.error('Error updating organization:', error);
      onError?.(error as Error);
    }
  };
  
  const handleDeleteOrg = async (orgId: string) => {
    if (!confirm('Are you sure you want to delete this organization? This action cannot be undone.')) {
      return;
    }
    
    try {
      await deleteOrganization(orgId);
      
      // Update local state
      await loadOrganizations();
      
      if (selectedOrgId === orgId) {
        setSelectedOrgId(organizations.length > 1 ? organizations[0].id : null);
      }
    } catch (error) {
      console.error('Error deleting organization:', error);
      onError?.(error as Error);
    }
  };
  
  const handleInviteUser = async () => {
    if (!selectedOrgId || !inviteEmail || !inviteRole) return;
    
    try {
      // In a real implementation, you would send an email invitation
      // For now, we'll just add the user directly
      await addUserToOrganization(selectedOrgId, inviteEmail, inviteRole as any);
      
      // Refresh members
      await loadOrganizations();
      
      // Clear form
      setInviteEmail('');
      setInviteRole('member');
    } catch (error) {
      console.error('Error inviting user:', error);
      onError?.(error as Error);
    }
  };
  
  const handleRemoveMember = async (userId: string) => {
    if (!selectedOrgId) return;
    
    if (!confirm('Are you sure you want to remove this member?')) {
      return;
    }
    
    try {
      await removeUserFromOrganization(selectedOrgId, userId);
      
      // Refresh members
      await loadOrganizations();
    } catch (error) {
      console.error('Error removing member:', error);
      onError?.(error as Error);
    }
  };
  
  const handleCreateOrgSuccess = async (orgId: string) => {
    // Refresh organizations
    if (!user) return;
    
    await loadOrganizations();
    setSelectedOrgId(orgId);
  };
  
  if (loading) {
    return <Loader />;
  }
  
  return (
    <Paper radius="md" p="xl" withBorder>
      <Title order={2} mb="md">Organization Management</Title>
      
      {organizations.length === 0 ? (
        <CreateOrganization onSuccess={handleCreateOrgSuccess} onError={onError} />
      ) : (
        <Tabs value={selectedOrgId || undefined} onChange={(value) => setSelectedOrgId(value as string)}>
          <Tabs.List>
            {organizations.map(org => (
              <Tabs.Tab key={org.id} value={org.id}>
                {org.name}
              </Tabs.Tab>
            ))}
          </Tabs.List>
          
          {organizations.map(org => (
            <Tabs.Panel key={org.id} value={org.id} pt="md">
              <Group justify="apart" mb="md">
                <Title order={3}>{org.name}</Title>
                
                <Menu position="bottom-end">
                  <Menu.Target>
                    <ActionIcon>
                      <IconDotsVertical size={16} />
                    </ActionIcon>
                  </Menu.Target>
                  
                  <Menu.Dropdown>
                    <Menu.Item 
                      leftSection={<IconEdit size={16} />}
                      onClick={() => handleEditOrg(org)}
                    >
                      Edit Organization
                    </Menu.Item>
                    
                    <Menu.Item 
                      leftSection={<IconTrash size={16} />}
                      onClick={() => handleDeleteOrg(org.id)}
                      color="red"
                    >
                      Delete Organization
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu>
              </Group>
              
              <Paper withBorder p="md" mb="md">
                <Title order={4} mb="md">
                  <Group>
                    <IconUserPlus size={20} />
                    <Text>Invite Member</Text>
                  </Group>
                </Title>
                
                <Group align="end">
                  <TextInput
                    label="Email Address"
                    placeholder="user@example.com"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    style={{ flex: 1 }}
                  />
                  
                  <Select
                    label="Role"
                    value={inviteRole}
                    onChange={(value) => setInviteRole(value || 'member')}
                    data={[
                      { value: 'admin', label: 'Admin' },
                      { value: 'member', label: 'Member' },
                      { value: 'guest', label: 'Guest' }
                    ]}
                    style={{ width: 150 }}
                  />
                  
                  <Button onClick={handleInviteUser}>
                    Invite
                  </Button>
                </Group>
              </Paper>
              
              <Paper withBorder p="md">
                <Title order={4} mb="md">
                  <Group>
                    <IconUsers size={20} />
                    <Text>Members ({orgMembers.length})</Text>
                  </Group>
                </Title>
                
                <Table>
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Email</th>
                      <th>Role</th>
                      <th>Joined</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orgMembers.map((member) => (
                      <tr key={member.uid}>
                        <td>
                          <Group>
                            <Avatar 
                              src={member.photoURL} 
                              radius="xl" 
                              size="sm"
                            />
                            <Text>{member.displayName || 'No name'}</Text>
                          </Group>
                        </td>
                        <td>{member.email}</td>
                        <td>
                          <Badge>
                            {member.organizations?.find(o => o.orgId === org.id)?.role || 'Member'}
                          </Badge>
                        </td>
                        <td>
                          {new Date(member.createdAt as string).toLocaleDateString()}
                        </td>
                        <td>
                          {member.uid !== org.ownerId && (
                            <ActionIcon 
                              color="red" 
                              onClick={() => handleRemoveMember(member.uid)}
                            >
                              <IconTrash size={16} />
                            </ActionIcon>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Paper>
            </Tabs.Panel>
          ))}
        </Tabs>
      )}
      
      {/* Edit Organization Modal */}
      <Modal
        opened={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        title="Edit Organization"
      >
        {editingOrg && (
          <Stack>
            <TextInput
              label="Organization Name"
              value={editingOrg.name}
              onChange={(e) => setEditingOrg({...editingOrg, name: e.target.value})}
            />
            
            <Group justify="right" mt="md">
              <Button variant="outline" onClick={() => setIsEditModalOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveOrg}>
                Save Changes
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </Paper>
  );
}; 